import { Drawer as BaseDrawer } from '@base-ui/react/drawer';
import styled from '@emotion/styled';

import { forMobile } from '../../responsive';
import { scrollbarStyles } from '../../scrollbar';
import { spacing } from '../../spacing';

const forwardProp = (prop: string) => !prop.startsWith('$');

export const StyledBackdrop = styled(BaseDrawer.Backdrop)(({ theme }) => ({
  position: 'fixed',
  minHeight: '100dvh',
  inset: 0,
  backgroundColor: theme.colors.black[100],
  opacity: 0.5,
  transition: 'opacity 150ms cubic-bezier(0.45, 1.005, 0, 1.005)',
  touchAction: 'none',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
  },
}));

export const StyledViewport = styled(BaseDrawer.Viewport)({
  position: 'fixed',
  inset: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  padding: spacing[8],
  pointerEvents: 'none',

  [forMobile]: {
    padding: 0,
  },
});

export const StyledPopup = styled(BaseDrawer.Popup, { shouldForwardProp: forwardProp })<{
  $maxWidth?: number;
}>(({ theme, $maxWidth }) => ({
  display: 'flex',
  boxSizing: 'border-box',
  width: '100%',
  maxWidth: $maxWidth ?? 704,
  height: '100%',
  padding: spacing[24],
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.raised,
  outline: 'none',
  touchAction: 'none',
  pointerEvents: 'auto',
  transform: 'translateX(var(--drawer-swipe-movement-x))',
  transition: 'transform 300ms cubic-bezier(0.32, 0.72, 0, 1)',

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateX(calc(100% + 8px))',
  },

  [forMobile]: {
    maxWidth: 'none',
    borderRadius: 0,
    backgroundColor: theme.colors.background.canvas,
  },
}));

export const StyledContent = styled(BaseDrawer.Content)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  flex: 1,
  minHeight: 0,
  gap: spacing[16],
});

export const Header = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing[8],
});

export const TitleRow = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  gap: spacing[8],
});

export const PopupContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box',
  overflowY: 'auto',
  flex: 1,
  minHeight: 0,
  padding: 4,
  margin: -4,
  width: 'calc(100% + 8px)',
  ...scrollbarStyles,
});

export const Actions = styled('div', { shouldForwardProp: forwardProp })<{
  $hasLeftAction: boolean;
}>(({ $hasLeftAction }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing[8],
  justifyContent: $hasLeftAction ? 'space-between' : 'flex-end',
}));

export const RightActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: spacing[8],
});
